/**
 * StrategyBuilderFooter.js
 *
 * Sticky footer that slides up when strikes are selected.
 * Shows selected legs with Buy/Sell toggle and quantity spinner.
 * Stays anchored at the bottom of the page; the table scrolls above it.
 */
import React from 'react';
import {
    Box, Paper, Typography, IconButton, ToggleButton, ToggleButtonGroup,
    TextField, Chip, Tooltip, Button, Divider, Slide,
} from '@mui/material';
import CloseIcon     from '@mui/icons-material/Close';
import DeleteIcon    from '@mui/icons-material/Delete';
import { alpha }     from '@mui/material/styles';
import useOptionChainStore from './useOptionChainStore';

function LegRow({ leg }) {
    const { updateLeg, removeLeg } = useOptionChainStore();
    const isBuy = leg.action === 'BUY';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: 1.5,
                bgcolor: 'action.hover',
                flexShrink: 0,
            }}
        >
            {/* Strike + side label */}
            <Chip
                label={`${leg.strike.toLocaleString('en-IN')} ${leg.side}`}
                size='small'
                variant='outlined'
                color={leg.side === 'CE' ? 'primary' : 'secondary'}
                sx={{ fontWeight: 700, minWidth: 100 }}
            />

            {/* Buy / Sell toggle */}
            <ToggleButtonGroup
                size='small'
                exclusive
                value={leg.action}
                onChange={(_, v) => v && updateLeg(leg.strike, leg.side, { action: v })}
            >
                <ToggleButton
                    value='BUY'
                    sx={{
                        px: 1.5, py: 0.25, fontSize: '0.7rem', fontWeight: 700,
                        '&.Mui-selected': {
                            bgcolor: (t) => alpha(t.palette.success.main, 0.2),
                            color: 'success.main',
                        },
                    }}
                >
                    B
                </ToggleButton>
                <ToggleButton
                    value='SELL'
                    sx={{
                        px: 1.5, py: 0.25, fontSize: '0.7rem', fontWeight: 700,
                        '&.Mui-selected': {
                            bgcolor: (t) => alpha(t.palette.error.main, 0.2),
                            color: 'error.main',
                        },
                    }}
                >
                    S
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Qty */}
            <TextField
                size='small'
                type='number'
                label='Lots'
                value={leg.qty}
                onChange={(e) => updateLeg(leg.strike, leg.side, { qty: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1, style: { width: 44, padding: '4px 6px', textAlign: 'center' } }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
            />

            {/* Remove */}
            <Tooltip title='Remove leg'>
                <IconButton size='small' onClick={() => removeLeg(leg.strike, leg.side)}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

export default function StrategyBuilderFooter() {
    const { selectedLegs, clearLegs } = useOptionChainStore();
    const open = selectedLegs.length > 0;

    return (
        <Slide direction='up' in={open} mountOnEnter unmountOnExit>
            <Paper
                elevation={8}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    borderTop: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: '12px 12px 0 0',
                    px: 3,
                    py: 1.5,
                    backdropFilter: 'blur(10px)',
                    bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
                }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant='subtitle2' fontWeight={700} sx={{ flexGrow: 1 }}>
                        Strategy Builder
                        <Chip
                            label={`${selectedLegs.length} leg${selectedLegs.length > 1 ? 's' : ''}`}
                            size='small'
                            color='primary'
                            sx={{ ml: 1, fontWeight: 700 }}
                        />
                    </Typography>

                    <Button
                        variant='contained'
                        color='primary'
                        size='small'
                        sx={{ mr: 1, fontWeight: 700 }}
                        onClick={() => {
                            // Hook point for future order placement
                            console.log('Place order:', selectedLegs);
                        }}
                    >
                        Place Order
                    </Button>

                    <Tooltip title='Clear all legs'>
                        <IconButton size='small' onClick={clearLegs}>
                            <DeleteIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                {/* Legs — horizontal scroll if many */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        pb: 0.5,
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'divider', borderRadius: 2,
                        },
                    }}
                >
                    {selectedLegs.map((leg) => (
                        <LegRow key={`${leg.strike}-${leg.side}`} leg={leg} />
                    ))}
                </Box>
            </Paper>
        </Slide>
    );
}
